import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Clock, Calendar, ChevronLeft, ChevronRight, FolderKanban,
} from 'lucide-react';
import { T, ROLES, springs } from '../lib/tokens';
import { supabase } from '../lib/supabase';
import { Glass } from '../components/glass/Glass';
import { RoleBadge } from '../components/primitives/Badge';
import { SectionHeader } from '../components/cards/SectionHeader';

const fmtDur = (m) => {
  if (!m || m === 0) return '0h';
  const h = Math.floor(m / 60);
  const mins = m % 60;
  if (h === 0) return `${mins}m`;
  return mins > 0 ? `${h}h ${mins}m` : `${h}h`;
};

function getWeekRange(offset = 0) {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) + offset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: monday.toISOString().split('T')[0], end: sunday.toISOString().split('T')[0] };
}

function getMonthRange(offset = 0) {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const last = new Date(today.getFullYear(), today.getMonth() + offset + 1, 0);
  return { start: first.toISOString().split('T')[0], end: last.toISOString().split('T')[0] };
}

// ─── Main TimeTrackingView ──────────────────────────────────────────────────

export const TimeTrackingView = ({ goals, tasks }) => {
  const [period, setPeriod] = useState('week'); // 'day' | 'week' | 'month'
  const [offset, setOffset] = useState(0);
  const [entries, setEntries] = useState([]);

  const today = new Date().toISOString().split('T')[0];

  const range = useMemo(() => {
    if (period === 'day') {
      const d = new Date();
      d.setDate(d.getDate() + offset);
      const key = d.toISOString().split('T')[0];
      return { start: key, end: key };
    }
    if (period === 'week') return getWeekRange(offset);
    return getMonthRange(offset);
  }, [period, offset]);

  const rangeLabel = useMemo(() => {
    if (period === 'day') {
      return new Date(range.start + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    }
    if (period === 'week') {
      const s = new Date(range.start + 'T12:00:00');
      const e = new Date(range.end + 'T12:00:00');
      return `${s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return new Date(range.start + 'T12:00:00').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }, [range, period]);

  // Fetch time entries from completed tasks
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('time_entries')
        .select('*, tasks(title, goal_id, role_id, goals(title, roles(slug, label, colour)), roles(slug, label, colour))')
        .gte('date', range.start)
        .lte('date', range.end)
        .order('date', { ascending: false });
      setEntries(data || []);
    };
    load();
  }, [range]);

  // Also compute from completed tasks that don't have time entries yet (fallback)
  const completedInRange = useMemo(() => {
    return tasks.filter(t => {
      if (!t.done_at) return false;
      const doneDate = new Date(t.done_at).toISOString().split('T')[0];
      return doneDate >= range.start && doneDate <= range.end;
    });
  }, [tasks, range]);

  // Merge: use time_entries if available, fall back to completed tasks
  const allEntries = useMemo(() => {
    const entryTaskIds = new Set(entries.map(e => e.task_id));
    const fromTasks = completedInRange
      .filter(t => !entryTaskIds.has(t.id))
      .map(t => ({
        id: `task-${t.id}`,
        task_id: t.id,
        date: new Date(t.done_at).toISOString().split('T')[0],
        duration_minutes: t.duration_minutes || 30,
        title: t.title,
        tasks: t,
      }));
    return [...entries, ...fromTasks].sort((a, b) => b.date?.localeCompare(a.date));
  }, [entries, completedInRange]);

  // Group by project
  const byProject = useMemo(() => {
    const map = {};
    for (const e of allEntries) {
      const goalId = e.tasks?.goal_id || '__ungrouped';
      const goalTitle = e.tasks?.goals?.title || 'Ungrouped';
      const roleSlug = e.tasks?.goals?.roles?.slug || e.tasks?.roles?.slug;
      if (!map[goalId]) map[goalId] = { title: goalTitle, roleSlug, entries: [], totalMin: 0 };
      map[goalId].entries.push(e);
      map[goalId].totalMin += e.duration_minutes || 0;
    }
    return Object.values(map).sort((a, b) => b.totalMin - a.totalMin);
  }, [allEntries]);

  // Group by workspace
  const byWorkspace = useMemo(() => {
    const map = {};
    for (const e of allEntries) {
      const slug = e.tasks?.goals?.roles?.slug || e.tasks?.roles?.slug || 'personal';
      if (!map[slug]) map[slug] = { slug, totalMin: 0 };
      map[slug].totalMin += e.duration_minutes || 0;
    }
    return Object.values(map).sort((a, b) => b.totalMin - a.totalMin);
  }, [allEntries]);

  // Group by day
  const byDay = useMemo(() => {
    const map = {};
    for (const e of allEntries) {
      if (!map[e.date]) map[e.date] = { date: e.date, totalMin: 0, entries: [] };
      map[e.date].totalMin += e.duration_minutes || 0;
      map[e.date].entries.push(e);
    }
    return Object.values(map).sort((a, b) => b.date.localeCompare(a.date));
  }, [allEntries]);

  const totalMinutes = allEntries.reduce((a, e) => a + (e.duration_minutes || 0), 0);
  const totalHours = (totalMinutes / 60).toFixed(1);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, letterSpacing: '-0.03em' }}>Time Tracking</h1>
          <p style={{ fontSize: 14, color: T.textMuted, marginTop: 4 }}>{rangeLabel}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            {['day', 'week', 'month'].map(p => (
              <button key={p} onClick={() => { setPeriod(p); setOffset(0); }} style={{
                padding: '6px 12px', border: 'none', cursor: 'pointer',
                background: period === p ? 'rgba(0,0,0,0.06)' : 'transparent',
                color: period === p ? T.text : T.textMuted,
                fontSize: 12, fontWeight: 500, fontFamily: 'inherit', textTransform: 'capitalize',
              }}>{p}</button>
            ))}
          </div>
          <button onClick={() => setOffset(0)} style={{
            padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
            background: offset === 0 ? 'rgba(0,0,0,0.04)' : 'transparent',
            cursor: 'pointer', fontSize: 12, fontWeight: 500, color: T.textMid, fontFamily: 'inherit',
          }}>Today</button>
          <button onClick={() => setOffset(o => o - 1)} style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)', background: 'transparent', cursor: 'pointer', color: T.textMid }}><ChevronLeft size={16} /></button>
          <button onClick={() => setOffset(o => o + 1)} style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)', background: 'transparent', cursor: 'pointer', color: T.textMid }}><ChevronRight size={16} /></button>
        </div>
      </div>

      {/* Total hours card */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 24 }}>
        <Glass style={{ padding: 18, textAlign: 'center' }} hover={false}>
          <div style={{ fontSize: 28, fontWeight: 700, color: T.amber }}>{totalHours}</div>
          <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 500 }}>Total hours</div>
        </Glass>
        <Glass style={{ padding: 18, textAlign: 'center' }} hover={false}>
          <div style={{ fontSize: 28, fontWeight: 700, color: T.green }}>{allEntries.length}</div>
          <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 500 }}>Tasks completed</div>
        </Glass>
        <Glass style={{ padding: 18, textAlign: 'center' }} hover={false}>
          <div style={{ fontSize: 28, fontWeight: 700, color: T.blue }}>{byProject.length}</div>
          <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 500 }}>Projects</div>
        </Glass>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
        {/* Left: by project */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <FolderKanban size={14} color={T.amber} />
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Hours by Project</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {byProject.map((proj, i) => {
              const r = proj.roleSlug ? ROLES[proj.roleSlug] : null;
              return (
                <Glass key={i} style={{ padding: 16 }} hover={false}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {r && <RoleBadge roleSlug={proj.roleSlug} small />}
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{proj.title}</span>
                    </div>
                    <span style={{ fontSize: 16, fontWeight: 700, color: r?.color || T.amber }}>{fmtDur(proj.totalMin)}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {proj.entries.slice(0, 8).map((e, j) => (
                      <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: j < proj.entries.length - 1 ? '1px solid rgba(0,0,0,0.03)' : 'none' }}>
                        <span style={{ fontSize: 10, color: T.textMuted, fontFamily: "'SF Mono', monospace", width: 55 }}>
                          {new Date(e.date + 'T12:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                        </span>
                        <span style={{ fontSize: 11, color: T.text, flex: 1 }}>{e.title || e.tasks?.title}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: T.textMid }}>{fmtDur(e.duration_minutes)}</span>
                      </div>
                    ))}
                    {proj.entries.length > 8 && (
                      <div style={{ fontSize: 10, color: T.textMuted, paddingTop: 4 }}>+{proj.entries.length - 8} more</div>
                    )}
                  </div>
                </Glass>
              );
            })}
            {byProject.length === 0 && (
              <Glass style={{ padding: 32, textAlign: 'center' }} hover={false}>
                <Clock size={24} color={T.textLight} style={{ marginBottom: 8 }} />
                <div style={{ fontSize: 13, color: T.textMuted }}>No completed tasks in this period.</div>
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>Complete tasks to track time.</div>
              </Glass>
            )}
          </div>
        </div>

        {/* Right: workspace summary + daily breakdown */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* By workspace */}
          <Glass style={{ padding: 18 }} hover={false}>
            <SectionHeader icon={Clock} iconBg={T.amberLight} iconColor={T.amber} title="By Workspace" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {byWorkspace.map(w => {
                const r = ROLES[w.slug];
                if (!r) return null;
                const pct = totalMinutes > 0 ? (w.totalMin / totalMinutes) * 100 : 0;
                return (
                  <div key={w.slug}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 500, color: T.text }}>{r.label}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: r.color }}>{fmtDur(w.totalMin)}</span>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.04)' }}>
                      <div style={{ height: '100%', borderRadius: 2, background: r.color, width: `${pct}%`, transition: 'width 0.3s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Glass>

          {/* Daily breakdown */}
          <Glass style={{ padding: 18 }} hover={false}>
            <SectionHeader icon={Calendar} iconBg={T.blueLight} iconColor={T.blue} title="Daily Breakdown" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {byDay.map(d => (
                <div key={d.date} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                  <span style={{ fontSize: 11, color: d.date === today ? T.amber : T.textMid, fontWeight: d.date === today ? 600 : 400 }}>
                    {new Date(d.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, color: T.textMuted }}>{d.entries.length} tasks</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{fmtDur(d.totalMin)}</span>
                  </div>
                </div>
              ))}
              {byDay.length === 0 && (
                <div style={{ fontSize: 12, color: T.textMuted, padding: '8px 0', textAlign: 'center' }}>No data</div>
              )}
            </div>
          </Glass>
        </div>
      </div>
    </motion.div>
  );
};
